import { useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, Paperclip, Search, X } from 'lucide-react';
import { toast } from '../../store/toastStore';
import { fileDropHandlers, splitBySize } from '../../utils/attachPasteDrop';
import { useUploadProgress } from '../../utils/useUploadProgress';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import Dialog from '../ui/Dialog';
import Field, { FormServerError } from '../ui/Field';
import {
  buildDiscussionPayload, discussionCustomerRows, discussionFormState, discussionStaffChoices,
  discussionSubmitBlock, effectivePickedIds, groupSingleOther, kindFromParticipants, matchesParticipantQuery, showParticipantSearch, type PickerPerson,
} from '../../utils/newDiscussionModel';
import EntityPicker from '../ui/EntityPicker';
import { engagementLabel } from '../../utils/engagementLabel';
import { countDiscussionParticipants, type DiscussionKind } from '../../utils/discussionParticipants';
import { openEngagements, type ChainEngagement } from '../engagements/engagementChainModel';
import { usePermissions } from '../../hooks/usePermissions';
import type { Contact, Ticket } from '../../types';
import { format } from 'date-fns';
import { useOpenDirectChat } from './ChatPeopleLists';
import { isCustomerRole, newThreadTitle } from '../../utils/chatScreenModel';

/**
 * P-36 (D-193) — "דיון חדש": its own short form, opened inside /discussions (and
 * from a customer's page, with the customer already chosen). Fields: subject,
 * first message, customer (optional), ONE participants list (OTE employees +
 * the chosen customer's people), "✓ N משתתפים" (at least 3, the opener
 * included), files (optional). No requester / owner / priority / due date /
 * engagement / source. No customer = an internal discussion of OTE.
 * After the save the caller navigates to the new discussion.
 *
 * C-17 (clarity, D-08): "עם מי?" comes first (chips + a search box instead of a
 * wall of checkboxes); the kind is not chosen but follows the participants
 * (the opener + one = שיחה, three or more = דיון) and is shown as one line.
 * Then the subject, the first message and the customer (optional). The API
 * payload is unchanged: `is_conversation` is sent as before, only computed.
 */
const FORM_ID = 'new-discussion-form';

export default function NewDiscussionModal({ initialOrganizationId, onClose, onCreated, onOpenChat, groupWording = false }: {
  /** The customer's page: the customer is already chosen. */
  initialOrganizationId?: string;
  /** Kept for callers (P-45b); since C-17 (D-08) the kind follows the participants, so it is not read. */
  initialKind?: DiscussionKind;
  onClose: () => void;
  /** D-326 round 2: opened from "+ קבוצה חדשה" - one wording ("קבוצה") instead of שיחה / דיון. */
  groupWording?: boolean;
  onCreated: (ticket: Ticket) => void;
  /** FIX-CHAT-THREADS-GROUPS: a group of one other person opens the direct chat with them instead (its id). */
  onOpenChat?: (ticketId: string) => void;
}) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  // D-196 (QX-141): the internal-only employee opens an INTERNAL discussion: no customer, OTE employees only.
  const { internalOnly } = usePermissions();
  const queryClient = useQueryClient();
  const [engagementId, setEngagementId] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [organizationId, setOrganizationId] = useState(initialOrganizationId ?? '');
  const [pickedIds, setPickedIds] = useState<string[]>([]);
  const [peopleQuery, setPeopleQuery] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState('');
  const [titleError, setTitleError] = useState<string | undefined>();
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: orgsData } = useQuery({
    queryKey: ['organizations', 'for-new-discussion'],
    queryFn: () => api.getOrganizations({ status: 'active', limit: 500 }),
    staleTime: 60_000,
    enabled: !internalOnly,
  });
  const { data: staffData } = useQuery({
    queryKey: ['users', 'ote-staff-for-new-discussion'],
    queryFn: () => api.getUsers({ ote_staff: true, active: true, limit: 500 }),
    staleTime: 60_000,
  });
  const { data: peopleData, isLoading: peopleLoading } = useQuery({
    queryKey: ['users', 'customer-people-for-new-discussion', organizationId],
    queryFn: () => api.getUsers({ organization_id: organizationId, active: true, limit: 500 }),
    enabled: !!organizationId && !internalOnly,
    staleTime: 60_000,
  });

  // P-42 (D-207 Q-191): ALL the customer's contacts, the primary one included.
  const { data: contactsData } = useQuery({
    queryKey: ['contacts', 'for-new-discussion', organizationId],
    // FIX-P42: the server caps a page at 200, so page until everything is loaded (no silent cut).
    queryFn: async () => {
      const all: Contact[] = [];
      let first: Awaited<ReturnType<typeof api.getContacts>> | null = null;
      for (let offset = 0; offset < 20000; offset += 200) {
        const res = await api.getContacts({ organization_id: organizationId, status: 'active', limit: 200, offset });
        first = first ?? res;
        const rows = (res?.data ?? []) as Contact[];
        all.push(...rows);
        if (rows.length < 200) break;
      }
      return { ...(first as object), data: all } as Awaited<ReturnType<typeof api.getContacts>>;
    },
    enabled: !!organizationId && !internalOnly,
    staleTime: 30_000,
  });

  // With a customer chosen an internal-only employee is not offered (D-196); the server checks too.
  const staff = useMemo(
    () => discussionStaffChoices(staffData?.data ?? [], user?.id, !!organizationId),
    [staffData, user?.id, organizationId],
  );
  const contacts = (contactsData?.data ?? []) as Contact[];
  const customerRows = useMemo(
    () => discussionCustomerRows(peopleData?.data ?? [], contacts, !!organizationId),
    [peopleData, contactsData, organizationId], // eslint-disable-line react-hooks/exhaustive-deps
  );
  // Only logins can take part; they are the ones that can be picked.
  const customerLogins = useMemo<PickerPerson[]>(
    () => customerRows.filter((r) => r.userId).map((r) => ({ id: r.userId as string, role: 'customer_viewer', name: r.name, email: r.email })),
    [customerRows],
  );
  const orgs = (orgsData?.data ?? []) as Array<{ id: string; name: string; is_internal?: boolean }>;

  // Another customer: the people of the old one (and an internal-only pick) drop out of the picks.
  const people = useMemo(() => [...staff, ...customerLogins], [staff, customerLogins]);
  const effectivePicked = effectivePickedIds(pickedIds, people);
  // C-17 (D-08): the kind follows the participants; the gate and the payload read the computed kind.
  // FIX-CHAT-THREADS-GROUPS: "+ קבוצה חדשה" is always a group (a discussion) - opener + one used to become a
  // 1:1 conversation and the server handed back the existing 1:1. A group needs 3; one other person = the
  // direct chat with them, offered in the form (חוי 11-05), never a group and never opened silently.
  const kind = groupWording ? 'discussion' : kindFromParticipants(countDiscussionParticipants(user?.id, effectivePicked));
  const isConv = kind === 'conversation';
  const gateKind = kind;
  // D-326 round 2: every label of the group form says "קבוצה" (no שיחה / דיון mix).
  const { count, enough, min } = discussionFormState(user?.id, effectivePicked, gateKind);
  const singleId = groupSingleOther(groupWording, user?.id, effectivePicked);
  const single = singleId ? people.find((p) => p.id === singleId) ?? null : null;
  const openChat = (id: string) => (onOpenChat ? onOpenChat(id) : onCreated({ id } as Ticket));
  const direct = useOpenDirectChat(openChat);
  const [threadBusy, setThreadBusy] = useState(false);
  const [threadError, setThreadError] = useState('');
  const openNewThread = async () => {
    if (!single || !user || threadBusy) return;
    setThreadBusy(true);
    setThreadError('');
    try {
      const payload = buildDiscussionPayload({
        openerId: user.id,
        title: newThreadTitle(title, single.name, format(new Date(), 'dd/MM/yyyy')),
        message,
        organizationId: isCustomerRole(single.role) ? organizationId : '',
        picked: [single.id],
        people: [single],
        kind: 'conversation',
        newThread: true,
      });
      const id = (await api.createTicket(payload)).data?.id ?? null;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['conversations'] }),
        queryClient.invalidateQueries({ queryKey: ['conversations-grouped'] }),
      ]);
      if (id) openChat(id); else setThreadError(t('chatScreen.openFailed'));
    } catch {
      setThreadError(t('chatScreen.openFailed'));
    } finally {
      setThreadBusy(false);
    }
  };

  // P-45b (D-210): a conversation with a customer may name the engagement its reply time goes to (optional).
  const { data: engagementsData } = useQuery({
    queryKey: ['engagements', 'for-new-conversation', organizationId],
    queryFn: () => api.getEngagements({ organization_id: organizationId, limit: 200 }),
    enabled: kind === 'conversation' && !!organizationId && !internalOnly,
    staleTime: 60_000,
  });
  // HN-4 (main, 28.10): the same source and rule as the task form - ALL the customer's engagements, the OPEN ones
  // (planning / active / on hold). The `status=active` filter hid every project (they start as "planning").
  const engagementOptions = openEngagements((engagementsData?.data ?? []) as ChainEngagement[])
    .map((e) => ({ value: e.id, label: engagementLabel(e.child_name ?? null, e.type, t) || e.id }));

  const changeOrganization = (id: string) => {
    setOrganizationId(id);
    setEngagementId('');
  };
  // FIX-B22 (D-216 QX-195): the search box narrows the rows; picks are kept.
  // C-17: with more than 8 people the rows show only while a name is typed (a short list shows at once).
  const peopleCount = staff.length + (organizationId ? customerRows.length : 0);
  const searchOn = showParticipantSearch(peopleCount);
  const query = peopleQuery;
  const showRows = !searchOn || query.trim() !== '';
  const shownStaff = staff.filter((u) => matchesParticipantQuery(u, query));
  const shownCustomerRows = customerRows.filter((r) => matchesParticipantQuery(r, query));
  const nothingFound = query.trim() !== '' && shownStaff.length === 0 && shownCustomerRows.length === 0;

  const toggle = (id: string) =>
    setPickedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const create = useMutation({
    mutationFn: () => {
      const payload = buildDiscussionPayload({
        openerId: user!.id, title, message, organizationId, picked: effectivePicked, people, kind,
        engagementId: engagementId || undefined,
        isGroup: groupWording,
      });
      return files.length > 0 ? api.createTicketWithFiles(payload, files) : api.createTicket(payload);
    },
    onSuccess: async (res) => {
      // The list of discussions (and the tickets list) show it at once.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['conversations'] }),
        queryClient.invalidateQueries({ queryKey: ['conversations-grouped'] }),
        queryClient.invalidateQueries({ queryKey: ['tickets'] }),
      ]);
      if (res?.data) onCreated(res.data);
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (create.isPending) return; // R-02c: no second discussion while the first one uploads
    setSubmitted(true);
    if (single) return; // FIX-CHAT-THREADS-GROUPS: one other person - the form offers the direct chat
    const block = discussionSubmitBlock(title, user?.id, effectivePicked, gateKind);
    if (block === 'title') {
      setTitleError(t('discussions.form.titleRequired'));
      return;
    }
    setTitleError(undefined);
    if (block) return;
    create.mutate();
  };

  // R-02: one path for picking, pasting and dropping; a file that is too big is named, never dropped silently.
  const addPicked = (picked: File[]) => {
    const { ok, tooBig } = splitBySize(picked);
    tooBig.forEach((b) => toast('error', t('media.tooLargeNamed', b)));
    setFileError(tooBig.map((b) => t('media.tooLargeNamed', b)).join(' · '));
    if (ok.length) setFiles((prev) => [...prev, ...ok]);
  };
  const addFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    addPicked(Array.from(e.target.files ?? []));
    e.target.value = '';
  };
  const drop = fileDropHandlers(addPicked);
  const uploadPct = useUploadProgress(create.isPending && files.length > 0);

  const checkRow = (id: string, label: string, hint?: string) => (
    <label key={id} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-surface-subtle">
      <input type="checkbox" checked={effectivePicked.includes(id)} onChange={() => toggle(id)} className="h-4 w-4 accent-primary-700" />
      <span className="text-sm text-text">{label}{hint && <span className="text-xs text-text-2"> (<bdi dir="ltr">{hint}</bdi>)</span>}</span>
    </label>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title={t(groupWording ? 'chatScreen.group.title' : 'discussions.conversationForm.title')}
      size="lg"
      dismissible={!create.isPending}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary flex-1 sm:flex-none">{t('common.cancel')}</button>
          <button type="submit" form={FORM_ID} disabled={create.isPending || (submitted && !enough)} aria-busy={create.isPending || undefined} className="btn btn-primary flex-1 sm:flex-none sm:min-w-36">
            {create.isPending && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            {groupWording ? t('chatScreen.group.submit') : isConv ? t('discussions.conversationForm.submit') : t('discussions.form.submit')}
          </button>
        </>
      )}
    >
      <form id={FORM_ID} onSubmit={handleSubmit} noValidate className="space-y-4">
        {/* C-17 (clarity, D-08): "עם מי?" first: the picks as chips, a search box, the matching people. */}
        <fieldset>
          <legend className="label">{t('discussions.form.whoLabel')}</legend>
          {effectivePicked.length > 0 && (
            <ul className="mb-2 flex flex-wrap gap-1.5" aria-label={t('discussions.form.pickedLabel')} data-testid="nd-picked">
              {effectivePicked.map((id) => {
                const person = people.find((p) => p.id === id);
                if (!person) return null;
                return (
                  <li key={id} className="inline-flex items-center gap-1 rounded-full bg-surface-100 py-0.5 pe-1 ps-2.5 text-xs text-text">
                    <bdi>{person.name}</bdi>
                    <button
                      type="button"
                      aria-label={t('discussions.form.removeParticipant', { name: person.name })}
                      onClick={() => toggle(id)}
                      className="rounded-full p-0.5 text-text-2 hover:bg-surface-200 hover:text-text"
                    >
                      <X size={12} aria-hidden="true" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="relative mb-2">
            <Search size={14} aria-hidden="true" className="pointer-events-none absolute inset-y-0 start-2.5 my-auto text-text-2" />
            <input
              type="search"
              className="input ps-8 text-sm"
              value={peopleQuery}
              onChange={(e) => setPeopleQuery(e.target.value)}
              placeholder={t('discussions.form.participantSearch')}
              aria-label={t('discussions.form.participantSearch')}
              autoComplete="off"
              data-testid="nd-participant-search"
            />
          </div>
          {showRows ? (
            <div className="max-h-56 overflow-y-auto rounded-lg border border-border p-1">
              <p className="px-2 pt-1 text-xs font-medium text-text-2">{t('discussions.form.oteTeam')}</p>
              {staff.length === 0 && <p className="px-2 py-1 text-xs text-text-2">{t('tickets.noOtherAgents')}</p>}
              {shownStaff.map((u) => checkRow(u.id, u.name))}
              {organizationId && (
                <>
                  <p className="px-2 pt-2 text-xs font-medium text-text-2">{t('discussions.form.customerPeople')}</p>
                  {peopleLoading && <p className="px-2 py-1 text-xs text-text-2">{t('common.loading')}</p>}
                  {!peopleLoading && customerRows.length === 0 && <p className="px-2 py-1 text-xs text-text-2">{t('discussions.form.noCustomerPeople')}</p>}
                  {shownCustomerRows.map((r) => {
                    if (r.userId) return checkRow(r.userId, r.name, r.email);
                    // No login: greyed, not part of the discussion. FIX-B21 (D-214 QX-175): no "יצירת גישה"
                    // button here any more (what such a contact shows is still open, Q-200).
                    return (
                      <div key={r.key} className="flex items-center gap-2 rounded-md px-2 py-1.5 opacity-60" data-testid="nd-contact-no-access">
                        <input type="checkbox" disabled className="h-4 w-4" aria-label={r.name} />
                        <span className="text-sm text-text-2">
                          {r.name}{r.email && <span className="text-xs"> (<bdi dir="ltr">{r.email}</bdi>)</span>}
                          <span className="text-xs"> · {r.accessDisabled ? t('discussions.form.accessDisabled') : t('discussions.form.noAccess')}</span>
                        </span>
                      </div>
                    );
                  })}
                </>
              )}
              {nothingFound && <p className="px-2 py-1 text-xs text-text-2">{t('discussions.form.participantSearchNone')}</p>}
            </div>
          ) : (
            <p className="px-1 text-xs text-text-2">{t('discussions.form.typeToSearch')}</p>
          )}
          {/* FIX-CHAT-THREADS-GROUPS: a group of one other person - the direct chat with them instead. */}
          {single ? (
            <div role="status" className="mt-2 rounded-lg bg-warning-50 px-3 py-2 text-sm text-warning-800" data-testid="nd-group-one-person">
              <p>{t('chatScreen.group.onePerson')}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={!!direct.busyId || threadBusy}
                  aria-busy={direct.busyId === single.id || undefined}
                  onClick={() => void direct.open({ id: single.id, name: single.name, role: single.role, email: single.email, organization_id: isCustomerRole(single.role) ? organizationId : null })}
                  data-testid="nd-group-open-direct"
                >
                  {t('chatScreen.group.openDirect', { name: single.name })}
                </button>
                {title.trim() !== '' && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={!!direct.busyId || threadBusy}
                    aria-busy={threadBusy || undefined}
                    onClick={() => void openNewThread()}
                    data-testid="nd-group-new-thread"
                  >
                    {t('chatScreen.group.newThreadWith', { subject: title.trim() })}
                  </button>
                )}
              </div>
              {(direct.error || threadError) && <p role="alert" className="mt-1 text-xs text-danger-text">{direct.error || threadError}</p>}
            </div>
          ) : (
          <p aria-live="polite" className={`mt-2 flex flex-wrap items-center gap-1.5 rounded-lg px-3 py-2 text-xs ${enough ? 'bg-surface-subtle text-text-2' : 'bg-warning-50 text-warning-800'}`} data-testid="nd-kind-line">
            {enough && <Check size={14} aria-hidden="true" />}
            <span>{enough ? t(groupWording ? 'chatScreen.group.kindLine' : isConv ? 'discussions.form.kindWillBeConversation' : 'discussions.form.kindWillBeDiscussion') : t(groupWording ? 'chatScreen.group.minHint' : 'discussions.conversationForm.minHint', { min })}</span>
            <span>· {t('discussions.form.participantCount', { count })}</span>
          </p>
          )}
        </fieldset>
        <Field id="nd-title" label={t('discussions.form.subject')} required error={titleError}>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={500} autoComplete="off" />
        </Field>
        <Field id="nd-message" label={t('discussions.form.firstMessage')}>
          <textarea className="input min-h-24" rows={4} value={message} onChange={(e) => setMessage(e.target.value)} {...drop} />
        </Field>
        {!internalOnly && (
          <Field id="nd-customer" label={t('discussions.form.customer')} hint={groupWording ? t('chatScreen.group.customerHint') : isConv ? t('discussions.conversationForm.customerHint') : t('discussions.form.customerHint')}>
            <EntityPicker
              value={organizationId}
              onChange={changeOrganization}
              disabled={!!initialOrganizationId}
              emptyLabel={groupWording ? t('chatScreen.group.noCustomer') : isConv ? t('discussions.conversationForm.noCustomer') : t('discussions.form.noCustomer')}
              options={orgs.filter((o) => !o.is_internal).map((o) => ({ value: o.id, label: o.name }))}
            />
          </Field>
        )}

        {isConv && organizationId && !internalOnly && (
          <Field id="nd-engagement" label={t('discussions.conversationForm.engagement')} hint={t('discussions.conversationForm.engagementHint')}>
            <EntityPicker
              value={engagementId}
              onChange={setEngagementId}
              emptyLabel={t('discussions.conversationForm.noEngagement')}
              options={engagementOptions}
            />
          </Field>
        )}

        <section aria-labelledby="nd-files-title" className="space-y-2" onDragOver={drop.onDragOver} onDrop={drop.onDrop}>
          <h3 id="nd-files-title" className="label">{t('discussions.form.files')}</h3>
          {files.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {files.map((f, i) => (
                <span key={`${f.name}-${i}`} className="flex items-center gap-1.5 rounded-lg bg-surface-100 ps-2 pe-1 py-1 text-xs text-surface-700">
                  <Paperclip size={12} aria-hidden="true" />
                  <span className="max-w-36 truncate" dir="auto">{f.name}</span>
                  <button type="button" aria-label={`${t('common.remove')}: ${f.name}`} onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))} className="rounded-xs p-1 text-surface-500 hover:bg-danger-50 hover:text-danger-700">
                    <X size={12} aria-hidden="true" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <button type="button" onClick={() => fileRef.current?.click()} className="btn btn-secondary btn-sm">
            <Paperclip size={14} aria-hidden="true" /> {t('tickets.addFile')}
          </button>
          <input ref={fileRef} type="file" multiple className="hidden" onChange={addFiles} />
          {fileError && <p role="alert" className="text-xs text-danger-700">{fileError}</p>}
          {uploadPct !== null && <p role="status" className="text-xs text-text-2">{t('media.createUploading', { pct: uploadPct })}</p>}
        </section>

        <FormServerError error={create.error} fallback={t('discussions.form.createError')} />
      </form>
    </Dialog>
  );
}
